import styled from 'styled-components';

interface ModalProps {
  isVisible: boolean;
  zIndex?: number;
}

interface LoadPage {
  keyWordExists: boolean;
}

interface TileProps {
  hasAnimation: boolean | undefined;
  transitionDelay: string;
  animationDelay: string;
  themeColor: string | null;
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;

  main {
    display: flex;
    flex-direction: column;
    gap: 5px;

    div.cube-row {
      display: flex;
      flex-direction: row;
      gap: 5px;
    }

    div.cube-row.error {
      -webkit-animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
      animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
      transform: translate3d(0, 0, 0);
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
      perspective: 1000px;
    }

    @keyframes shake {
      10%,
      90% {
        transform: translate3d(-1px, 0, 0);
      }
      20%,
      80% {
        transform: translate3d(2px, 0, 0);
      }
      30%,
      50%,
      70% {
        transform: translate3d(-4px, 0, 0);
      }
      40%,
      60% {
        transform: translate3d(4px, 0, 0);
      }
    }
  }
`;

export const LoadingPage = styled.div<LoadPage>`
  background-color: rgb(216, 216, 216);
  display: ${(props) => (props.keyWordExists ? 'none' : 'flex')};
  z-index: 11;
  color: #333;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  min-width: 30vw;

  @media (max-width: 1200px) and (max-height: 900px) {
    width: 600px;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 920px) and (max-height: 1400px) {
    width: 600px;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 540px) and (max-height: 915px) {
    width: 100vw;
    margin-bottom: 1.5rem;
  }

  img {
    width: 100%;
    max-width: 350px;
    min-width: 250px;
  }

  h1 {
    text-transform: uppercase;
    font-size: 1.2rem;
    animation: loading 3s infinite ease-in-out;
    margin-right: 10px;
    @keyframes loading {
      0% {
        opacity: 1;
      }
      50% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
  }
`;

export const ModalWarning = styled.div<ModalProps>`
  position: absolute;
  z-index: ${(props) => props.zIndex};
  top: 30px;
  left: 30px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.backgroundWordle};
  display: ${(props) => (props.isVisible ? 'flex' : 'none')};
  transition: all 0.3s ease-in;
  padding: 10px 20px 0 20px;
  h1 {
    color: ${({ theme }) => theme.background};
    font-weight: bold;
    font-size: 1.8rem;
    @media (max-width: 550px) {
      font-size: 1.6rem;
    }
    @media (max-width: 510px) {
      font-size: 1.3rem;
    }
  }
  @media (max-width: 550px) {
    left: 15px;
  }
  @media (max-width: 510px) {
    left: 5px;
  }
`;

export const ModalError = styled.div<ModalProps>`
  position: absolute;
  z-index: 10;
  height: calc(100% - 57px);
  width: calc(100% - 200px);
  scroll-behavior: unset;
  background-color: ${({ theme }) => theme.background};
  display: ${(props) => (props.isVisible ? 'flex' : 'none')};
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: 900px) {
    height: calc(100vh - 114px);
    width: 100vw;
  }

  main {
    background-color: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    width: 50%;
    max-height: 85vh;
    padding: 30px 20px;
    border-radius: 5px;
    text-align: center;
    -ms-overflow-style: none;
    scrollbar-width: none;
    overflow-y: scroll;
    overflow-x: hidden;
    align-items: center;
    border: 2px solid ${({ theme }) => theme.text};

    ::-webkit-scrollbar {
      display: none;
    }
    @media (max-width: 1000px) {
      width: 70%;
    }

    @media (max-width: 800px) {
      width: 80%;
    }
    @media (max-width: 600px) {
      width: 70%;
    }
    @media (max-width: 500px) {
      width: 90%;
      margin-bottom: 20px;
    }
    @media (max-width: 800px) and (max-height: 700px) {
      max-height: 80vh;
    }
    @media (max-width: 450px) and (max-height: 900px) {
      max-height: 80vh;
    }
    @media (max-width: 450px) and (max-height: 750px) {
      max-height: 70vh;
    }

    h2 {
      color: ${({ theme }) => theme.text};
    }

    h2 {
      font-size: 1.8rem;
      margin-bottom: -5px;
    }

    div {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    span {
      font-size: 1.8rem;
      color: #8c4e9a;
      text-transform: uppercase;
      font-weight: bold;
    }

    img {
      margin-top: 20px;
      width: 55%;
    }
  }
`;

export const GameContainer = styled.section`
  position: relative;
  box-sizing: border-box;

  main {
    display: flex;
    height: 100%;
  }

  @media (max-height: 700px) {
    margin-bottom: 15px;
  }

  @media (max-height: 645px) {
    margin-bottom: 10px;
  }
`;

export const Tile = styled.div<TileProps>`
  div {
    transition: all 0.4s ease;
    transition-delay: ${(props) => props.transitionDelay};
    animation-duration: 0.7s;
    animation-delay: ${(props) => props.animationDelay};
    animation-name: ${(props) => (props.hasAnimation ? 'wink' : '')};
  }

  div.letter {
    text-transform: uppercase;
    display: flex;
    justify-content: center;
    width: 15vw;
    max-width: 110px;
    height: 15vw;
    max-height: 110px;
    border: 1px solid;
    border-image-slice: 1;
    border-width: 2px;
    border-image-source: ${(props) => props.themeColor};

    h1 {
      font-size: 4.5rem;
    }

    @media (max-width: 1750px) and (max-height: 820px) {
      max-width: 95px;
      max-height: 95px;

      h1 {
        font-size: 3.5rem;
      }
    }

    @media (max-width: 1280px) and (max-height: 900px) {
      max-width: 75px;
      max-height: 75px;

      h1 {
        font-size: 3rem;
      }
    }

    @media (max-width: 1200px) and (max-height: 900px) {
      max-width: 40px;
      max-height: 40px;

      h1 {
        font-size: 1.75rem;
      }
    }

    @media (max-width: 920px) and (max-height: 1400px) {
      max-width: 90px;
      max-height: 90px;

      h1 {
        font-size: 3.5rem;
      }
    }

    @media (max-width: 920px) and (max-height: 1180px) {
      max-width: 85px;
      max-height: 85px;

      h1 {
        font-size: 4rem;
      }
    }

    @media (max-width: 400px) and (max-height: 700px) {
      max-width: 45px;
      max-height: 45px;

      h1 {
        font-size: 2rem;
      }
    }

    @media (max-width: 540px) and (max-height: 720px) {
      max-width: 45px;
      max-height: 45px;

      h1 {
        font-size: 2rem;
      }
    }

    @media (max-width: 415px) and (max-height: 915px) {
      max-width: 55px;
      max-height: 55px;

      h1 {
        font-size: 2.5rem;
      }
    }

    @media (max-width: 415px) and (max-height: 720px) {
      max-width: 45px;
      max-height: 45px;

      h1 {
        font-size: 2rem;
      }
    }
  }

  div.correct {
    background: ${({ theme }) => theme.green};
    h1 {
      color: ${({ theme }) => theme.white};
    }
  }

  div.present {
    background: ${({ theme }) => theme.yellow};
    h1 {
      color: ${({ theme }) => theme.white};
    }
  }

  div.absent {
    background: ${({ theme }) => theme.gray};
    h1 {
      color: ${({ theme }) => theme.white};
    }
  }

  h1 {
    display: flex;
    height: 15vw;
    max-height: 60px;
    font-size: 36px;
    font-weight: bold;
    color: ${({ theme }) => theme.text};

    transition: all 0.4s ease;
    transition-delay: ${(props) => props.transitionDelay};
    animation-duration: 0.7s;
    animation-delay: ${(props) => props.animationDelay};
    animation-name: ${(props) => (props.hasAnimation ? 'wink' : '')};

    @media (max-width: 390px) {
      font-size: 35px;
    }

    @media (max-width: 360px) {
      font-size: 34px;
    }

    @media (max-width: 345px) {
      font-size: 32px;
    }

    @media (max-width: 330px) {
      font-size: 30px;
    }

    @media (max-width: 315px) {
      font-size: 28px;
    }

    @media (max-width: 300px) {
      font-size: 26px;
    }

    @media (max-width: 280px) {
      font-size: 24px;
    }

    @media (min-width: 900px) and (min-height: 700px) {
      font-size: 37px;
    }
  }

  @keyframes wink {
    100% {
      transform: rotateY(180deg);
    }
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;

  background-color: ${({ theme }) => theme.background};

  > p {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    width: 100%;
    color: #000;
    margin-top: auto;

    svg {
      margin-left: 8px;
      transform: rotateX(180deg);
    }
  }

  @media (min-width: 1201px) {
    > p {
      display: none;
    }
  }
`;

export const GameContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: calc(100% - 21vw);

  .mobileKeyboard {
    margin-bottom: 2rem;
  }

  @media (min-width: 1201px) {
    &::before {
      content: '';
      width: 100%;
      height: 1.25rem;
      background: #9fa7f7;
    }
  }

  > span {
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  @media (min-width: 1201px) {
    > span {
      display: none;
    }

    .mobileKeyboard {
      display: none;
    }
  }

  > div {
    display: flex;
    width: 100%;
    justify-content: center;

    @media (max-width: 1200px) {
      margin-top: 1rem;
    }
  }
`;
